import { TAGS_ACTION_TYPES } from "../actions/tagsActions";
import { DEFAULT_PAGINATION_LIMIT, TAG_UPDATE_CONFIG } from "../consts";
import { TAG_UPDATE_TYPE } from "../../TagsEditor/consts";

// Helper function to recursively update tag children at any nesting level
const updateTagChildrenRecursive = (tags, tagId, children) => {
  return tags.map((entityTag) => {
    // Handle both EntityTag format (with nested tag) and direct Tag format
    const tag = entityTag?.tag || entityTag;

    if (tag.id === tagId) {
      // If it's an EntityTag, update the nested tag's childTags
      if (entityTag?.tag) {
        return {
          ...entityTag,
          tag: {
            ...entityTag.tag,
            childTags: children,
          },
        };
      }
      // Otherwise update directly
      return {
        ...entityTag,
        childTags: children,
      };
    }
    // If this tag has children, recursively update them
    if (tag.childTags?.tags && tag.childTags.tags.length > 0) {
      // If it's an EntityTag, update the nested tag
      if (entityTag?.tag) {
        return {
          ...entityTag,
          tag: {
            ...entityTag.tag,
            childTags: {
              ...tag.childTags,
              tags: updateTagChildrenRecursive(
                tag.childTags.tags,
                tagId,
                children
              ),
            },
          },
        };
      }
      // Otherwise update directly
      return {
        ...entityTag,
        childTags: {
          ...tag.childTags,
          tags: updateTagChildrenRecursive(tag.childTags.tags, tagId, children),
        },
      };
    }
    return entityTag;
  });
};

// Initial State
export const initialTagsState = {
  selectedTabType: null,
  availableTabTypes: [],

  // Existing fields
  tags: [],
  tagTypes: [],
  loading: false,
  countsLoading: false,
  error: null,

  // Updated pagination to be per tag type
  pagination: {
    limit: DEFAULT_PAGINATION_LIMIT,
    offset: 0,
    totalCount: 0,
  },
  // Per-tag-type pagination state
  tagTypePagination: {},
};

// Reducer
export const tagsReducer = (state = initialTagsState, action) => {
  switch (action.type) {
    case TAGS_ACTION_TYPES.SET_TAGS_DATA:
      return {
        ...state,
        tags: action.payload,
        loading: false,
        error: null,
      };

    case TAGS_ACTION_TYPES.SET_PAGINATION:
      return {
        ...state,
        pagination: {
          ...state.pagination,
          ...action.payload,
        },
      };

    case TAGS_ACTION_TYPES.UPDATE_TAG_CHILDREN: {
      const { tagId, children } = action.payload;
      return {
        ...state,
        tags: updateTagChildrenRecursive(state.tags, tagId, children),
      };
    }

    case TAGS_ACTION_TYPES.SET_ERROR:
      return {
        ...state,
        loading: false,
        error: action.payload,
      };

    case TAGS_ACTION_TYPES.SET_SELECTED_TAB_TYPE:
      return {
        ...state,
        selectedTabType: action.payload,
      };

    case TAGS_ACTION_TYPES.SET_AVAILABLE_TAB_TYPES:
      return {
        ...state,
        availableTabTypes: action.payload,
      };

    case TAGS_ACTION_TYPES.SET_COUNTS_LOADING:
      return {
        ...state,
        countsLoading: action.payload,
      };

    case TAGS_ACTION_TYPES.SET_TAG_TYPE_PAGINATION:
      return {
        ...state,
        tagTypePagination: {
          ...state.tagTypePagination,
          [action.payload.tagType]: action.payload.pagination,
        },
      };

    case TAGS_ACTION_TYPES.REMOVE_TAG: {
      const tagIdToRemove = action.payload;

      // Check if the tag exists in the current state
      const tagExists = state?.tags?.find((t) => t.tagId === tagIdToRemove);
      if (!tagExists) {
        return state;
      }

      const newTotalCount = Math.max(0, state.pagination.totalCount - 1);
      const { limit = DEFAULT_PAGINATION_LIMIT, offset = 0 } = state.pagination;

      // If removing this tag empties the current tab, we need to update available tabs
      if (newTotalCount === 0) {
        // Remove the current tab from available tabs
        const updatedAvailableTabs = state.availableTabTypes.filter(
          (tab) => tab.id !== state.selectedTabType
        );

        // Select the first available tab, or null if none remain
        const newSelectedTabType =
          updatedAvailableTabs.length > 0 ? updatedAvailableTabs[0]?.id : null;

        return {
          ...state,
          tags: state.tags.filter((t) => t.tagId !== tagIdToRemove),
          pagination: {
            ...state.pagination,
            offset: 0,
            totalCount: newTotalCount,
          },
          availableTabTypes: updatedAvailableTabs,
          selectedTabType: newSelectedTabType,
        };
      }

      // Filter out the removed tag
      const remainingTags = state.tags.filter((t) => t.tagId !== tagIdToRemove);

      // Check if current page will be empty after removal
      // If so, navigate to the previous page
      let newOffset = offset;
      if (offset >= newTotalCount) {
        // Calculate the offset for the last page with items
        newOffset = Math.max(
          0,
          Math.floor((newTotalCount - 1) / limit) * limit
        );
      }
      // Case 2: Current page becomes empty and not on first page
      else if (remainingTags.length === 0 && newTotalCount > 0 && offset > 0) {
        // Move to previous page
        newOffset = Math.max(0, offset - limit);
      }

      // If there are still tags remaining in the current tab, update the count
      const updatedAvailableTabs = state.availableTabTypes.map((tab) =>
        tab.id === state.selectedTabType
          ? { ...tab, count: Math.max(0, tab.count - 1) }
          : tab
      );

      return {
        ...state,
        tags: remainingTags,
        pagination: {
          ...state.pagination,
          offset: newOffset,
          totalCount: newTotalCount,
        },
        availableTabTypes: updatedAvailableTabs,
      };
    }

    case TAGS_ACTION_TYPES.UPDATE_TAG: {
      const { type, payload } = action.payload;

      // Get configuration for this update type
      const config = TAG_UPDATE_CONFIG[type];
      if (!config) {
        // Unknown update type, return state unchanged
        return state;
      }

      const { idField, fieldsToUpdate } = config;

      // For TAG type, we need to find by nested tag.id
      // For ENTITY_TAG type, we find by tagId
      let tagExists = false;
      if (type === TAG_UPDATE_TYPE.TAG) {
        // Find by nested tag.id since we're updating the Tag itself
        tagExists = state.tags?.find((t) => {
          const tag = t?.tag || t;
          return tag.id === payload[idField];
        });
      } else if (type === TAG_UPDATE_TYPE.ENTITY_TAG) {
        // Find by tagId for EntityTag updates
        tagExists = state.tags?.find((t) => t[idField] === payload[idField]);
      }

      if (!tagExists) {
        // Tag doesn't exist, return state unchanged
        return state;
      }

      // Update the tags array
      return {
        ...state,
        tags: state.tags.map((entityTag) => {
          const tag = entityTag?.tag || entityTag;

          // Check if this is the tag to update
          let isMatch = false;
          if (type === TAG_UPDATE_TYPE.TAG) {
            isMatch = tag.id === payload[idField];
          } else if (type === TAG_UPDATE_TYPE.ENTITY_TAG) {
            isMatch = entityTag[idField] === payload[idField];
          }

          if (!isMatch) {
            return entityTag;
          }

          // Apply updates based on type
          if (type === TAG_UPDATE_TYPE.TAG) {
            // Update nested tag fields
            const updatedFields = {};
            fieldsToUpdate.forEach((field) => {
              if (payload[field] !== undefined) {
                updatedFields[field] = payload[field];
              }
            });

            return {
              ...entityTag,
              tag: {
                ...entityTag.tag,
                ...updatedFields,
              },
            };
          }

          if (type === TAG_UPDATE_TYPE.ENTITY_TAG) {
            // Update entity tag fields
            const updatedFields = {};
            fieldsToUpdate.forEach((field) => {
              if (payload[field] !== undefined) {
                updatedFields[field] = payload[field];
              }
            });

            return {
              ...entityTag,
              ...updatedFields,
            };
          }

          return entityTag;
        }),
      };
    }

    default:
      return state;
  }
};
